import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../App.css";

import { onAuthStateChanged } from "firebase/auth";

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { auth, db } from "../firebase";

function Refer() {
  const [copied, setCopied] = useState(false);
  const [realReferralMembers, setRealReferralMembers] = useState([]);
  const [referralCode, setReferralCode] = useState("");
  const [referralLink, setReferralLink] = useState("");
  const [loadingTeam, setLoadingTeam] = useState(true);

  const commissionRates = {
    1: 0.15,
    2: 0.05,
    3: 0.02,
  };

  const totalReferrals = realReferralMembers.length;

  const activeReferrals = realReferralMembers.filter(
    (member) => Number(member.totalDeposit || 0) > 0
  ).length;

  const totalReferralDeposit = realReferralMembers.reduce(
    (total, member) =>
      total + Number(member.totalDeposit || 0),
    0
  );

  const totalReferralEarnings = realReferralMembers.reduce(
    (total, member) => {
      const depositAmount = Number(member.totalDeposit || 0);

      const commissionRate =
        commissionRates[member.level] || 0;

      return total + depositAmount * commissionRate;
    },
    0
  );

  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(
      auth,
      (currentUser) => {
        if (unsubscribeUser) {
          unsubscribeUser();
          unsubscribeUser = null;
        }

        if (!currentUser) {
          setReferralCode("");
          setReferralLink("");
          setRealReferralMembers([]);
          setLoadingTeam(false);
          return;
        }

        const userRef = doc(
          db,
          "users",
          currentUser.uid
        );

        unsubscribeUser = onSnapshot(
          userRef,
          (snapshot) => {
            if (!snapshot.exists()) {
              setReferralCode("");
              setReferralLink("");
              setRealReferralMembers([]);
              setLoadingTeam(false);
              return;
            }

            const userData = snapshot.data();

            const code =
              userData.referralCode ||
              `ST${currentUser.uid
                .slice(0, 6)
                .toUpperCase()}`;

            if (!userData.referralCode) {
              updateDoc(userRef, {
                referralCode: code,
              }).catch((error) => {
                console.error(
                  "Referral code save error:",
                  error
                );
              });
            }

            setReferralCode(code);

            setReferralLink(
              `${window.location.origin}/signup?ref=${code}`
            );
          },
          (error) => {
            console.error(
              "Current user snapshot error:",
              error
            );

            setLoadingTeam(false);
          }
        );
      }
    );

    return () => {
      unsubscribeAuth();

      if (unsubscribeUser) {
        unsubscribeUser();
      }
    };
  }, []);

  const splitIntoChunks = (items, chunkSize = 30) => {
    const chunks = [];

    for (
      let index = 0;
      index < items.length;
      index += chunkSize
    ) {
      chunks.push(
        items.slice(index, index + chunkSize)
      );
    }

    return chunks;
  };

  const getUsersByParentCodes = async (
    parentCodes,
    level
  ) => {
    const cleanCodes = [
      ...new Set(
        parentCodes
          .map((code) =>
            String(code || "")
              .trim()
              .toUpperCase()
          )
          .filter(Boolean)
      ),
    ];

    if (cleanCodes.length === 0) {
      return [];
    }

    const codeChunks = splitIntoChunks(
      cleanCodes,
      30
    );

    const allMembers = [];

    for (const codeChunk of codeChunks) {
      let membersQuery;

      if (codeChunk.length === 1) {
        membersQuery = query(
          collection(db, "users"),
          where(
            "referredBy",
            "==",
            codeChunk[0]
          )
        );
      } else {
        membersQuery = query(
          collection(db, "users"),
          where(
            "referredBy",
            "in",
            codeChunk
          )
        );
      }

      const membersSnapshot =
        await getDocs(membersQuery);

      membersSnapshot.docs.forEach(
        (memberDocument) => {
          allMembers.push({
            id: memberDocument.id,
            ...memberDocument.data(),
            level,
          });
        }
      );
    }

    const uniqueMembers = Array.from(
      new Map(
        allMembers.map((member) => [
          member.id,
          member,
        ])
      ).values()
    );

    return uniqueMembers;
  };

  useEffect(() => {
    if (!referralCode) {
      setRealReferralMembers([]);
      setLoadingTeam(false);
      return;
    }

    let cancelled = false;

    const loadReferralTeam = async () => {
      try {
        setLoadingTeam(true);

        /*
          LEVEL 1
          Direct users who used current user's code
        */
        const levelOneMembers =
          await getUsersByParentCodes(
            [referralCode],
            1
          );

        /*
          LEVEL 2
          Users who used Level 1 members' codes
        */
        const levelOneCodes =
          levelOneMembers
            .map(
              (member) =>
                member.referralCode
            )
            .filter(Boolean);

        const levelTwoMembers =
          await getUsersByParentCodes(
            levelOneCodes,
            2
          );

        /*
          LEVEL 3
          Users who used Level 2 members' codes
        */
        const levelTwoCodes =
          levelTwoMembers
            .map(
              (member) =>
                member.referralCode
            )
            .filter(Boolean);

        const levelThreeMembers =
          await getUsersByParentCodes(
            levelTwoCodes,
            3
          );

        const completeTeam = [
          ...levelOneMembers,
          ...levelTwoMembers,
          ...levelThreeMembers,
        ];

        const uniqueTeam = Array.from(
          new Map(
            completeTeam.map((member) => [
              member.id,
              member,
            ])
          ).values()
        );

        uniqueTeam.sort((first, second) => {
          if (first.level !== second.level) {
            return first.level - second.level;
          }

          const firstTime =
            first.createdAt?.toMillis
              ? first.createdAt.toMillis()
              : 0;

          const secondTime =
            second.createdAt?.toMillis
              ? second.createdAt.toMillis()
              : 0;

          return secondTime - firstTime;
        });

        if (!cancelled) {
          setRealReferralMembers(uniqueTeam);
        }

        console.log(
          "Level 1 Members:",
          levelOneMembers
        );

        console.log(
          "Level 2 Members:",
          levelTwoMembers
        );

        console.log(
          "Level 3 Members:",
          levelThreeMembers
        );
      } catch (error) {
        console.error(
          "Referral team load error:",
          error
        );

        if (!cancelled) {
          setRealReferralMembers([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTeam(false);
        }
      }
    };

    loadReferralTeam();

    return () => {
      cancelled = true;
    };
  }, [referralCode]);

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(
        referralLink
      );

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error(
        "Referral link copy error:",
        error
      );

      alert(
        "Referral link copy nahi ho saka."
      );
    }
  };

  const shareOnWhatsApp = () => {
    const message = encodeURIComponent(
      `Join SnapTube with my referral link and start earning: ${referralLink}`
    );

    window.open(
      `https://wa.me/?text=${message}`,
      "_blank"
    );
  };

  const shareOnTelegram = () => {
    const url =
      encodeURIComponent(referralLink);

    const text = encodeURIComponent(
      "Join SnapTube with my referral link and start earning."
    );

    window.open(
      `https://t.me/share/url?url=${url}&text=${text}`,
      "_blank"
    );
  };

  const shareOnFacebook = () => {
    const url =
      encodeURIComponent(referralLink);

    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      "_blank"
    );
  };

  const getMemberCommissionRate = (
    level
  ) => {
    return commissionRates[level] || 0;
  };

  return (
    <div className="app referral-page">
      <header className="page-header">
        <Link
          to="/dashboard"
          className="back-button"
        >
          ←
        </Link>

        <div>
          <h1>Referral Program</h1>
          <p>
            Invite friends and earn more
          </p>
        </div>
      </header>

      <section className="referral-hero-card">
        <div className="referral-hero-icon">
          🎁
        </div>

        <div>
          <span>
            Total Referral Earnings
          </span>

          <h2>
            $
            {totalReferralEarnings.toFixed(
              2
            )}
          </h2>

          <p>
            Earn commission from three
            referral levels
          </p>
        </div>
      </section>

      <section className="referral-card">
        <div className="referral-section-title">
          <div>
            <h2>Commission Levels</h2>

            <p>
              Earn from your complete
              referral team
            </p>
          </div>
        </div>

        <div className="commission-levels">
          <div className="commission-level level-one">
            <span>Level 1</span>
            <strong>15%</strong>
            <small>
              Direct referrals
            </small>
          </div>

          <div className="commission-level level-two">
            <span>Level 2</span>
            <strong>5%</strong>
            <small>Second level</small>
          </div>

          <div className="commission-level level-three">
            <span>Level 3</span>
            <strong>2%</strong>
            <small>Third level</small>
          </div>
        </div>
      </section>

      <section className="referral-card">
        <div className="referral-section-title">
          <div>
            <h2>
              Your Referral Link
            </h2>

            <p>
              Share your link and grow
              your team
            </p>
          </div>

          <span className="referral-code-badge">
            {referralCode}
          </span>
        </div>

        <div className="referral-link-box">
          <span>{referralLink}</span>

          <button
            type="button"
            onClick={copyReferralLink}
            disabled={!referralLink}
          >
            {copied
              ? "Copied"
              : "Copy"}
          </button>
        </div>

        <div className="referral-share-buttons">
          <button
            type="button"
            className="share-button whatsapp-share"
            onClick={shareOnWhatsApp}
            disabled={!referralLink}
          >
            WhatsApp
          </button>

          <button
            type="button"
            className="share-button telegram-share"
            onClick={shareOnTelegram}
            disabled={!referralLink}
          >
            Telegram
          </button>

          <button
            type="button"
            className="share-button facebook-share"
            onClick={shareOnFacebook}
            disabled={!referralLink}
          >
            Facebook
          </button>
        </div>
      </section>

      <section className="referral-stats-grid">
        <div className="referral-stat-card">
          <span>Total Referrals</span>
          <strong>
            {totalReferrals}
          </strong>
        </div>

        <div className="referral-stat-card">
          <span>
            Active Referrals
          </span>

          <strong>
            {activeReferrals}
          </strong>
        </div>

        <div className="referral-stat-card">
          <span>Total Deposit</span>

          <strong>
            $
            {totalReferralDeposit.toFixed(
              2
            )}
          </strong>
        </div>

        <div className="referral-stat-card">
          <span>
            Referral Earnings
          </span>

          <strong>
            $
            {totalReferralEarnings.toFixed(
              2
            )}
          </strong>
        </div>
      </section>

      <section className="referral-card">
        <div className="referral-section-title">
          <div>
            <h2>Referral Team</h2>

            <p>
              Your latest referral
              members
            </p>
          </div>

          <button
            type="button"
            className="referral-filter-button"
          >
            All Levels
          </button>
        </div>

        <div className="referral-list">
          {loadingTeam && (
            <p>
              Referral team loading...
            </p>
          )}

          {!loadingTeam &&
            realReferralMembers.length ===
              0 && (
              <p>
                No referral members yet.
              </p>
            )}

          {!loadingTeam &&
            realReferralMembers.map(
              (member, index) => {
                const depositAmount =
                  Number(
                    member.totalDeposit ||
                      0
                  );

                const commissionRate =
                  getMemberCommissionRate(
                    member.level
                  );

                const memberBonus =
                  depositAmount *
                  commissionRate;

                return (
                  <article
                    className="referral-member"
                    key={member.id}
                  >
                    <div className="referral-member-number">
                      {index + 1}
                    </div>

                    <div className="referral-member-info">
                      <h3>
                        {member.name ||
                          "SnapTube User"}
                      </h3>

                      <p>
                        {member.referralCode ||
                          member.id.slice(
                            0,
                            10
                          )}
                      </p>

                      <p className="referral-join-date">
                        🗓️ Joined:{" "}
                        {member.createdAt
                          ?.toDate
                          ? member.createdAt
                              .toDate()
                              .toLocaleDateString(
                                "en-GB"
                              )
                          : "No Date"}
                      </p>

                      <div className="referral-member-badges">
                        <span>
                          Level{" "}
                          {member.level}
                        </span>

                        <span className="active-referral-status">
                          {depositAmount > 0
                            ? "Active"
                            : "Pending"}
                        </span>
                      </div>
                    </div>

                    <div className="referral-member-money">
                      <strong>
                        $
                        {depositAmount.toFixed(
                          2
                        )}
                      </strong>

                      <span>
                        Bonus: $
                        {memberBonus.toFixed(
                          2
                        )}
                      </span>
                    </div>
                  </article>
                );
              }
            )}
        </div>
      </section>

      <section className="referral-note-card">
        <h3>Important Note</h3>

        <p>
          Referral commission will be
          added after the referred user
          completes an approved deposit.
        </p>
      </section>

      <nav className="bottom-nav">
        <Link
          to="/dashboard"
          className="nav-item"
        >
          <span className="nav-icon">
            🏠
          </span>

          <span>Dashboard</span>
        </Link>

        <Link
          to="/deposit"
          className="nav-item"
        >
          <span className="nav-icon">
            💰
          </span>

          <span>Deposit</span>
        </Link>

        <Link
          to="/machine"
          className="nav-item"
        >
          <span className="nav-icon">
            ▶️
          </span>

          <span>Machine</span>
        </Link>

        <Link
          to="/refer"
          className="nav-item active"
        >
          <span className="nav-icon">
            👥
          </span>

          <span>Referral</span>
        </Link>

        <Link
          to="/profile"
          className="nav-item"
        >
          <span className="nav-icon">
            👤
          </span>

          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
}

export default Refer;